import React from 'react';
import { 
  TrendingUp, 
  Lock, 
  Maximize2, 
  DollarSign,
  CheckCircle,
  Star,
  Zap,
  Shield
} from 'lucide-react';

const ClientBenefits = () => {
  const benefits = [
    {
      icon: TrendingUp,
      title: 'Strategic Clarity',
      description: 'Clear technology roadmap aligned with business objectives',
      gradient: 'from-logo-green2 to-logo-green3',
      stats: '3-5 Year Roadmap',
    },
    {
      icon: Lock,
      title: 'Improved Security',
      description: 'Reduced risk with enterprise-grade cybersecurity measures',
      gradient: 'from-logo-darkblue to-logo-teal',
      stats: '99.9% Threat Prevention',
    },
    {
      icon: Maximize2,
      title: 'Scalable Systems',
      description: 'Infrastructure designed to grow with your business',
      gradient: 'from-logo-lightblue to-logo-green3',
      stats: '10x Scalability',
    },
    {
      icon: DollarSign,
      title: 'Cost Optimization',
      description: 'Reduced IT spend with optimized technology investments',
      gradient: 'from-logo-yellow to-logo-green',
      stats: '30% Cost Reduction',
    },
  ];

  const outcomes = [
    'Reduced downtime by 95%',
    'Increased productivity by 40%',
    'Faster time-to-market',
    'Improved compliance posture',
    'Better decision making',
    'Enhanced customer experience',
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Premium Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-logo-navy via-logo-darkblue to-logo-lightblue">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-logo-lightblue/20 rounded-full blur-3xl"></div>
        </div>
        <div className="tech-pattern opacity-10"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 glass-effect px-4 py-2 rounded-full mb-4">
            <Star className="text-logo-yellow" size={16} />
            <span className="text-sm font-medium text-white">Why Choose Us</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Client{' '}
            <span className="bg-gradient-to-r from-logo-green to-logo-yellow bg-clip-text text-transparent">
              Benefits
            </span>
          </h2>
          
          <p className="text-blue-100 max-w-3xl mx-auto text-base sm:text-lg">
            Transforming your technology from a cost center to a strategic growth accelerator
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <div
                key={index}
                className="group relative glass-effect rounded-2xl p-8 hover:scale-105 
                         transition-all duration-500 cursor-default overflow-hidden"
              >
                {/* Hover Effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} 
                               opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>

                {/* Icon */}
                <div className="relative mb-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${benefit.gradient} 
                                 flex items-center justify-center transform group-hover:scale-110
                                 group-hover:rotate-3 transition-all duration-500 shadow-lg`}>
                    <Icon className="text-white" size={28} />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-white mb-3 group-hover:gradient-text-primary 
                             transition-all duration-300">
                  {benefit.title}
                </h3>
                <p className="text-blue-100 mb-4">
                  {benefit.description}
                </p>
                <div className="text-sm font-medium text-logo-yellow">
                  {benefit.stats}
                </div>

                {/* Decorative Element */}
                <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-white/5 rounded-full 
                              group-hover:scale-150 transition-transform duration-500"></div>
              </div>
            );
          })}
        </div>

        {/* Outcomes Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left - Key Outcomes */}
          <div className="glass-effect rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Zap className="text-logo-yellow mr-2" size={24} />
              Key Outcomes
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {outcomes.map((outcome, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <CheckCircle className="text-logo-green flex-shrink-0" size={18} />
                  <span className="text-blue-100 text-sm">{outcome}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - ROI Calculator Preview */}
          <div className="glass-effect rounded-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white">ROI Calculator</h3>
              <Shield className="text-logo-green2" size={24} />
            </div>
            
            <div className="space-y-4 mb-6">
              <div>
                <div className="flex justify-between text-sm text-blue-100 mb-1">
                  <span>Current IT Spend</span>
                  <span>$100k</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div className="bg-logo-green w-3/4 h-2 rounded-full"></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm text-blue-100 mb-1">
                  <span>Optimized Cost</span>
                  <span>$70k</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div className="bg-logo-yellow w-1/2 h-2 rounded-full"></div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <div className="text-3xl font-bold text-logo-green mb-2">30%</div>
              <div className="text-sm text-blue-100 mb-4">Average Cost Reduction</div>
              <a href="#contact" className="btn-accent inline-block w-full text-center">
                Calculate Your Savings
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientBenefits;